import { z } from 'zod';

const footageMatchStateSchema = z.enum(['linked', 'new_video', 'occupied', 'pending', 'no_video']);
const footageStatusSchema = z.enum([
  'recording',
  'completed',
  'no_video',
  'review_match',
  'linked',
  'link_manually',
]);

function validateCalendarAndUtcRange(
  value: { dateFrom?: string; dateTo?: string; startedAtFrom?: string; startedAtTo?: string },
  context: z.RefinementCtx,
) {
  const hasCalendarRange = value.dateFrom !== undefined || value.dateTo !== undefined;
  const hasUtcRange = value.startedAtFrom !== undefined || value.startedAtTo !== undefined;
  if (hasCalendarRange && hasUtcRange) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Calendar date filters cannot be combined with UTC timestamp filters.',
    });
  }
}

/** Closed, frontend-owned query input for `GET /field-log-visits`. */
export const fieldLogVisitsRequestSchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.number().int().positive().optional(),
    createBy: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    timeZone: z.string().optional(),
    startedAtFrom: z.string().optional(),
    startedAtTo: z.string().optional(),
    createdAtFrom: z.string().optional(),
    createdAtTo: z.string().optional(),
    venueName: z.string().optional(),
    cameraOpsCode: z.array(z.string()).optional(),
    hasSessions: z.boolean().optional(),
    footageMatch: z.array(footageMatchStateSchema).optional(),
    footageStatus: z.array(footageStatusSchema).optional(),
    operatorPresence: z.enum(['present', 'missing']).optional(),
    recording: z.boolean().optional(),
    organizationId: z.array(z.string()).optional(),
    projectId: z.array(z.string()).optional(),
    toleranceMinutes: z.number().int().nonnegative().optional(),
    include: z.array(z.enum(['recordingRange', 'footageMatch'])).optional(),
  })
  .strict()
  .superRefine(validateCalendarAndUtcRange);

/** Closed, frontend-owned query input for `GET /field-log-sessions`. */
export const fieldLogSessionsRequestSchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.number().int().positive().optional(),
    organizationId: z.array(z.string()).optional(),
    projectId: z.array(z.string()).optional(),
    runner: z.array(z.string()).optional(),
    opsCode: z.array(z.string()).optional(),
    footageMatch: z.array(footageMatchStateSchema).optional(),
    footageStatus: z.array(footageStatusSchema).optional(),
    include: z.array(z.literal('footageMatch')).optional(),
    environmentId: z.string().optional(),
    environmentPresence: z.enum(['present', 'missing']).optional(),
    operatorId: z.string().optional(),
    operatorPresence: z.enum(['present', 'missing']).optional(),
    recording: z.boolean().optional(),
    linked: z.boolean().optional(),
    q: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    timeZone: z.string().optional(),
    startedAtFrom: z.string().optional(),
    startedAtTo: z.string().optional(),
  })
  .strict()
  .superRefine((value, context) => {
    validateCalendarAndUtcRange(value, context);
    if (
      value.footageStatus !== undefined &&
      (value.linked !== undefined || value.footageMatch !== undefined)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'footageStatus cannot be combined with linked or footageMatch.',
      });
    }
  });
